import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInvite } from "@/hooks/useInvite";
import { CandidateFlow } from "@/shell/CandidateFlow";
import { Button, Spinner } from "@/components/primitives";
import { Waveform } from "@/components/Waveform";
import { InviteError } from "./InviteError";
import { candidateApi } from "@/lib/candidateApi";

// C2 — 60-second demo session (try before sign-in).
// Shows a single pre-canned question from the AI persona.
// Timer auto-advances after 60 seconds. No real Gemini call.
const DEMO_DURATION = 60;

// Bundled speech: "Tell me, in two minutes or less, what is drawing you to this
// specific role, and why now in your career?" Public demo needs no authenticated TTS.
const DEMO_AUDIO = "/audio/demo-question.mp3";

const PERSONA_NAME = "James Forsythe";
const PERSONA_TITLE = "Senior Partner, Executive Search";
const PERSONA_BIO =
  "25 years placing commercial leaders at FTSE 100 and PE-backed businesses. Blunt, precise, high standards. He will probe any answer that sounds rehearsed.";

type Phase = "intro" | "question" | "listening" | "done";

export function DemoSession() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data, error, loading } = useInvite(token);

  const [phase, setPhase] = useState<Phase>("intro");
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [questionHeard, setQuestionHeard] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(DEMO_DURATION);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      audio.onplaying = audio.onpause = audio.onwaiting = audio.onended = audio.onerror = null;
      audio.pause();
    }
    setAudioPlaying(false);
  }, []);

  function playQuestion() {
    stopAudio();
    setPhase("question");
    setQuestionHeard(false);
    setAudioError(null);
    const audio = new Audio(DEMO_AUDIO);
    audioRef.current = audio;
    const current = () => audioRef.current === audio;
    const failed = () => {
      if (!current()) return;
      stopAudio();
      setQuestionHeard(false);
      setAudioError("The demo question could not play. Check your sound and try again, or skip the demo.");
    };
    audio.onplaying = () => { if (current()) setAudioPlaying(true); };
    audio.onpause = audio.onwaiting = () => { if (current()) setAudioPlaying(false); };
    audio.onended = () => {
      if (!current()) return;
      setAudioPlaying(false);
      setQuestionHeard(true);
    };
    audio.onerror = failed;
    // Called directly by Begin/Retry so browsers retain the playback user gesture.
    try { void audio.play().catch(failed); } catch { failed(); }
  }

  function startListening() {
    if (!questionHeard) return;
    stopAudio();
    clearTimer();
    setPhase("listening");
    setSecondsLeft(DEMO_DURATION);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearTimer();
          setPhase("done");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  useEffect(() => () => { clearTimer(); stopAudio(); }, [clearTimer, stopAudio]);

  async function proceed() {
    clearTimer();
    stopAudio();
    if (!data || !token) return;
    if (data.verified) {
      navigate(`/invite/${token}/home`);
    } else {
      // First-time visitor — the invite token itself proves identity.
      const res = await candidateApi.authByToken(token);
      if (res.ok) {
        navigate(`/invite/${token}/${res.next}`);
      } else {
        navigate(`/invite/${token}/verify`);
      }
    }
  }

  if (loading) {
    return (
      <CandidateFlow>
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      </CandidateFlow>
    );
  }
  if (error || !data) return <InviteError message={error ?? "Invite not found."} />;

  const agency = data.agencyName || "Your consultant";
  const lightLogo = data.branding?.logoUrlLight || undefined;
  const logoFilter = data.branding?.logoFilter;

  return (
    <CandidateFlow agencyName={data.agencyName} logoUrl={lightLogo} logoFilter={logoFilter}>
      {/* Intro phase */}
      {phase === "intro" && (
        <div className="space-y-6">
          <div>
            <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">
              Before you begin
            </p>
            <h1 className="text-xl font-semibold leading-snug text-ink">
              Try one question — it takes 60 seconds
            </h1>
          </div>
          <p className="text-sm leading-relaxed text-slate-500">
            The first time you answer under any pressure, a third of your working memory is consumed by
            anxiety. By the third practice, that cost drops substantially.
          </p>
          <p className="text-sm font-medium text-ink-2">
            This is your first practice moment. Answer as if it were the real interview.
          </p>

          {/* Persona card */}
          <div className="rounded-lg border border-hair bg-cream p-4">
            <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Your interviewer for this question
            </p>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink text-[15px] font-bold text-cream">
                JF
              </div>
              <div>
                <p className="text-sm font-semibold text-ink">{PERSONA_NAME}</p>
                <p className="text-xs text-slate-500">{PERSONA_TITLE}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{PERSONA_BIO}</p>
              </div>
            </div>
          </div>

          <Button variant="accent" className="w-full" onClick={playQuestion}>
            Begin →
          </Button>
          <button
            type="button"
            onClick={proceed}
            className="w-full text-center text-sm text-slate-400 hover:text-slate-600"
          >
            Skip — go straight to my session
          </button>
        </div>
      )}

      {/* Question phase */}
      {phase === "question" && (
        <div className="space-y-6">
          <div className="flex flex-col items-center rounded-lg border border-hair bg-cream px-5 py-8 text-center">
            <span className="relative mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ink text-[15px] font-bold text-cream">
              JF
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-cream bg-good" />
            </span>
            <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">
              {PERSONA_NAME} asks
            </p>
            <Waveform active={audioPlaying} />
          </div>
          {audioError && (
            <div role="alert" className="rounded-md border border-hair bg-paper-2 p-4 text-sm text-ink">
              <p>{audioError}</p>
              <Button variant="ghost" className="mt-3" onClick={playQuestion}>Retry audio</Button>
            </div>
          )}
          <p className="text-sm leading-relaxed text-slate-500">
            When you're ready, press the button below and answer aloud for up to 60 seconds.
          </p>
          <Button variant="accent" className="w-full" onClick={startListening} disabled={!questionHeard}>
            <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-white opacity-90" />
            Start speaking
          </Button>
          <button
            type="button"
            onClick={proceed}
            className="w-full text-center text-sm text-slate-400 hover:text-slate-600"
          >
            Skip demo
          </button>
        </div>
      )}

      {/* Listening phase */}
      {phase === "listening" && (
        <div className="space-y-6">
          <div className="flex flex-col items-center rounded-lg border border-hair bg-cream px-5 py-8 text-center">
            <span className="relative mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ink text-[15px] font-bold text-cream">
              JF
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-cream bg-good" />
            </span>
            <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">
              LISTENING
            </p>
            <Waveform active />
          </div>

          {/* Listening indicator */}
          <div className="flex items-center justify-between rounded-lg border border-accent bg-accent-50 px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-accent" />
              <span className="text-sm font-medium text-accent-ink">Listening…</span>
            </div>
            <span className="font-mono text-sm font-semibold text-accent">
              {secondsLeft}s
            </span>
          </div>

          <button
            type="button"
            onClick={() => { clearTimer(); stopAudio(); setPhase("done"); }}
            className="w-full rounded-pill border border-slate-300 py-2.5 text-sm font-medium text-slate-500 hover:border-slate-400 hover:text-ink"
          >
            I'm done speaking
          </button>
        </div>
      )}

      {/* Done phase */}
      {phase === "done" && (
        <div className="space-y-6">
          <div>
            <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-good">
              That was your first practice moment
            </p>
            <h1 className="text-xl font-semibold leading-snug text-ink">
              Good. The anxiety you felt just now is normal.
            </h1>
          </div>
          <div className="rounded-lg border border-hair bg-cream p-4 text-sm leading-relaxed text-slate-500">
            <p>
              Research shows that anxiety cost drops substantially after the third simulation.
              Candidates who complete three full sessions perform measurably better in the real interview.
            </p>
            <p className="mt-3 font-medium text-ink-2">
              Your full scorecard and structured feedback will be ready after each session —
              shared with both you and {agency}.
            </p>
          </div>
          <Button variant="accent" className="w-full" onClick={proceed}>
            Yes — save my results and begin →
          </Button>
          <button
            type="button"
            onClick={proceed}
            className="w-full text-center text-sm text-slate-400 hover:text-slate-600"
          >
            Maybe later
          </button>
        </div>
      )}
    </CandidateFlow>
  );
}
